/** @prettier */

/**
 * パンくずリスト
 */

import {useEffect, useState} from 'react';
import {Typography, Breadcrumbs, Box} from '@mui/material';
import {useLocation, useParams, Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//API import
import {
  getCompositionsSitesBySiteID,
  getCompositionsGatewayByGatewayID,
  getCompositionsGroupsByGroupID,
} from '../API/API_Collection';
//interface import
import * as apiIF from '../Type/api';
//Style import
import '../css/common/common.css';

import {routeList} from '../routes/PrivateRoute';

/**
 * パンくずリストコンポーネント
 * 引数情報から各機器の名称をパンくずリスト形式で表示
 * @param {string} siteName サイトの名前（APIの呼び出し回数を減らすため、極力入力すること）
 * @param {apiIF.res.getCompositionsGatewayByGatewayID} gwInfo ゲートウェイの情報（APIの呼び出し回数を減らすため、極力入力すること）
 * @param {string} groupName グループの名前（APIの呼び出し回数を減らすため、極力入力すること）
 * @returns JSX
 */
const RegistrationBreadcrumbs = (props: {
  siteName?: string;
  gwInfo?: apiIF.res.getCompositionsGatewayByGatewayID;
  groupName?: string;
}) => {
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {siteID, gwID, systemType, ouID, floorID, groupID} = useParams();

  const [siteName, setSiteName] = useState<string | null>(null); //サイトの名前
  const [gwInfo, setGWInfo] =
    useState<apiIF.res.getCompositionsGatewayByGatewayID | null>(null); //ゲートウェイの情報
  const [groupName, setGroupName] = useState<string | null>(null); //グループの名前
  const [breadcrumbsLink, setBreadcrumbsLink] = useState<
    {name: string | undefined; path: string}[]
  >([]); //パンくずリスト情報

  const trans = useTranslation().t; //翻訳関数
  const i18n = useTranslation().i18n;

  //パスパラメータの通信設定から表示する文字に変換するテーブル
  const systemTypeTable: {[key: string]: string} = {
    sl1: trans('RegistrationBreadcrumbs.linkTxt.superlink1'),
    sl2: trans('RegistrationBreadcrumbs.linkTxt.superlink2'),
    rt1: trans('RegistrationBreadcrumbs.linkTxt.remoteTerminal1'),
    rt2: trans('RegistrationBreadcrumbs.linkTxt.remoteTerminal2'),
    modbus: trans('RegistrationBreadcrumbs.linkTxt.modbus'),
  };

  useEffect(() => {
    //サイトIDを取得できない場合
    if (siteID === undefined) return;

    if (props.siteName === undefined) {
      //サイトの情報を取得
      getCompositionsSitesBySiteID(siteID).then(response => {
        setSiteName(response['site-name']);
      });
    } else {
      //既にサイトの名前を取得している場合
      setSiteName(props.siteName);
    }

    if (gwID !== undefined) {
      //既にゲートウェイの情報を取得している場合
      if (props.gwInfo !== undefined) {
        setGWInfo(props.gwInfo);
        return;
      }
      //ゲートウェイの情報を取得※サニタイズされた文字を戻していない
      getCompositionsGatewayByGatewayID(siteID, gwID).then(response => {
        setGWInfo(response);
      });
    } else if (groupID !== undefined) {
      //既にグループの名前を取得している場合
      if (props.groupName !== undefined) {
        setGroupName(props.groupName);
        return;
      }
      getCompositionsGroupsByGroupID(siteID, groupID).then(response => {
        setGroupName(response['group-name']);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.siteName, props.gwInfo, props.groupName]);

  //パンくずリスト作成
  useEffect(() => {
    //サイトIDを取得できない場合
    //サイトの名前を取得できない場合
    if (siteID === undefined || siteName === null) return;

    let tempBreadcrumbsLink: {name: string | undefined; path: string}[] = [
      {
        name: `${trans('RegistrationBreadcrumbs.linkTxt.mySites')}`,
        path: routeList.mySite.path,
      },
    ];

    //ゲートウェイ一覧画面の表示項目
    //フロア一覧画面の表示項目
    //Hyozan用グループ一覧画面の表示項目
    //Modbus用グループ一覧画面の表示項目
    if (
      location.pathname.endsWith('gw-setting') ||
      location.pathname.endsWith('floor-detail') ||
      location.pathname.endsWith('hyozan-group-registration') ||
      location.pathname.endsWith('modbus-group-registration')
    ) {
      tempBreadcrumbsLink.push({
        name: siteName,
        path: '/',
      });
      setBreadcrumbsLink(tempBreadcrumbsLink);
      return;
    }

    if (gwID !== undefined && gwInfo !== null) {
      //構成情報画面
      tempBreadcrumbsLink.push({
        name: siteName,
        path: `/pages/my-site/site-detail/${siteID}/gw-setting${location.search}`,
      });
      //通信系統番号画面の表示項目
      if (location.pathname.endsWith('communication')) {
        tempBreadcrumbsLink.push({name: gwInfo['gateway-name'], path: '/'});
        setBreadcrumbsLink(tempBreadcrumbsLink);
        return;
      }

      //通信設定を取得できない場合
      if (systemType === undefined) return;

      if (location.pathname.endsWith('sl-ou-registration')) {
        //SL通信室外機一覧画面の表示項目
        tempBreadcrumbsLink.push(
          {
            name: gwInfo['gateway-name'],
            path: `/pages/my-site/site-detail/${siteID}/gw-setting/${gwID}/communication${location.search}`,
          },
          {name: systemTypeTable[systemType], path: '/'}
        );
      } else if (location.pathname.endsWith('sl-iu-registration')) {
        //室外機IDを取得できない場合
        if (ouID === undefined) return;
        //SL通信室内機一覧画面の表示項目
        tempBreadcrumbsLink.push(
          {
            name: gwInfo['gateway-name'],
            path: `/pages/my-site/site-detail/${siteID}/gw-setting/${gwID}/communication${location.search}`,
          },
          {
            name: systemTypeTable[systemType],
            path: `/pages/my-site/site-detail/${siteID}/gw-setting/${gwID}/communication/${systemType}/sl-ou-registration${location.search}`,
          },
          {name: slOUName(gwInfo, ouID), path: '/'}
        );
      } else if (location.pathname.endsWith('sl-other-registration')) {
        //その他空調機一覧画面の表示項目
        tempBreadcrumbsLink.push(
          {
            name: gwInfo['gateway-name'],
            path: `/pages/my-site/site-detail/${siteID}/gw-setting/${gwID}/communication${location.search}`,
          },
          {
            name: systemTypeTable[systemType],
            path: `/pages/my-site/site-detail/${siteID}/gw-setting/${gwID}/communication/${systemType}/sl-ou-registration${location.search}`,
          },
          {
            name: `${trans('RegistrationBreadcrumbs.linkTxt.interface')}`,
            path: '/',
          }
        );
      } else if (location.pathname.endsWith('rt-registration')) {
        //熱源機一覧画面の表示項目
        tempBreadcrumbsLink.push(
          {
            name: gwInfo['gateway-name'],
            path: `/pages/my-site/site-detail/${siteID}/gw-setting/${gwID}/communication${location.search}`,
          },
          {name: systemTypeTable[systemType], path: '/'}
        );
      } else if (location.pathname.endsWith('rt-iu-registration')) {
        //室外機IDを取得できない場合
        if (ouID === undefined) return;
        //RT通信室内機一覧画面の表示項目
        tempBreadcrumbsLink.push(
          {
            name: gwInfo['gateway-name'],
            path: `/pages/my-site/site-detail/${siteID}/gw-setting/${gwID}/communication${location.search}`,
          },
          {
            name: systemTypeTable[systemType],
            path: `/pages/my-site/site-detail/${siteID}/gw-setting/${gwID}/communication/${systemType}/rt-registration${location.search}`,
          },
          {name: rtOUName(gwInfo, ouID), path: '/'}
        );
      } else if (location.pathname.endsWith('modbus-ou-registration')) {
        //Modbus通信室外機一覧画面の表示項目
        tempBreadcrumbsLink.push(
          {
            name: gwInfo['gateway-name'],
            path: `/pages/my-site/site-detail/${siteID}/gw-setting/${gwID}/communication${location.search}`,
          },
          {name: systemTypeTable[systemType], path: '/'}
        );
      } else if (location.pathname.endsWith('modbus-iu-registration')) {
        //室外機IDを取得できない場合
        if (ouID === undefined) return;
        //Modbus通信室内機一覧画面の表示項目（SL通信一覧からのアクセス）
        tempBreadcrumbsLink.push(
          {
            name: gwInfo['gateway-name'],
            path: `/pages/my-site/site-detail/${siteID}/gw-setting/${gwID}/communication${location.search}`,
          },
          {
            name: systemTypeTable[systemType],
            path: `/pages/my-site/site-detail/${siteID}/gw-setting/${gwID}/communication/${systemType}/modbus-ou-registration${location.search}`,
          },
          {name: modbusOUName(gwInfo, ouID), path: '/'}
        );
      }
      setBreadcrumbsLink(tempBreadcrumbsLink);
      return;
    } else if (floorID !== undefined) {
      if (location.pathname.endsWith('group-registration')) {
        //グループ一覧画面の表示項目
        tempBreadcrumbsLink.push(
          {
            name: siteName,
            path: `/pages/my-site/site-detail/${siteID}/floor-detail${location.search}`,
          },
          {
            name:
              Number(floorID) < 0
                ? `${trans(
                    `RegistrationBreadcrumbs.linkTxt.floor.basement${Math.abs(
                      Number(floorID)
                    )}`
                  )}`
                : `${trans(
                    `RegistrationBreadcrumbs.linkTxt.floor.floor${floorID}`
                  )}`,
            path: '/',
          }
        );
      } else if (location.pathname.endsWith('group-layout-registration')) {
        //グループIDを取得できない場合
        //グループの名前を取得できない場合
        if (groupID === undefined || groupName === null) return;
        //グループレイアウト画面の表示項目
        tempBreadcrumbsLink.push(
          {
            name: siteName,
            path: `/pages/my-site/site-detail/${siteID}/floor-detail${location.search}`,
          },
          {
            name:
              Number(floorID) < 0
                ? `${trans(
                    `RegistrationBreadcrumbs.linkTxt.floor.basement${Math.abs(
                      Number(floorID)
                    )}`
                  )}`
                : `${trans(
                    `RegistrationBreadcrumbs.linkTxt.floor.floor${floorID}`
                  )}`,
            path: `/pages/my-site/site-detail/${siteID}/floor-detail/${floorID}/group-registration${location.search}`,
          },
          {name: groupName, path: '/'}
        );
      }
      setBreadcrumbsLink(tempBreadcrumbsLink);
      return;
    } else {
      if (location.pathname.endsWith('hyozan-group-layout-registration')) {
        //グループIDを取得できない場合
        //グループの名前を取得できない場合
        if (groupID === undefined || groupName === null) return;
        //Hyozan用グループレイアウト画面の表示項目
        tempBreadcrumbsLink.push(
          {
            name: siteName,
            path: `/pages/my-site/site-detail/${siteID}/hyozan-group-registration`,
          },
          {name: groupName, path: '/'}
        );
      } else if (
        location.pathname.endsWith('modbus-group-layout-registration')
      ) {
        //グループIDを取得できない場合
        //グループの名前を取得できない場合
        if (groupID === undefined || groupName === null) return;
        //Modbus用グループレイアウト画面の表示項目
        tempBreadcrumbsLink.push(
          {
            name: siteName,
            path: `/pages/my-site/site-detail/${siteID}/modbus-group-registration`,
          },
          {name: groupName, path: '/'}
        );
      }
      setBreadcrumbsLink(tempBreadcrumbsLink);
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, siteName, gwInfo, groupName, i18n.language]);

  /**
   * SL通信設定室外機の名前を取得
   * @param gwInfo ゲートウェイの情報
   * @param ouID 室外機ID
   * @returns 室外機の名前
   */
  const slOUName = (
    gwInfo: apiIF.res.getCompositionsGatewayByGatewayID,
    ouID: string
  ) => {
    for (const unitInfo of gwInfo.compositions['sl-system-1']['sl-unit']) {
      if (unitInfo['outdoor-id'] === ouID) {
        return unitInfo['outdoor-unit-name'];
      }
    }
    for (const unitInfo of gwInfo.compositions['sl-system-2']['sl-unit']) {
      if (unitInfo['outdoor-id'] === ouID) {
        return unitInfo['outdoor-unit-name'];
      }
    }
    return '';
  };

  /**
   * SL通信設定室外機の名前を取得
   * @param gwInfo ゲートウェイの情報
   * @param ouID 室外機ID
   * @returns 室外機の名前
   */
  const rtOUName = (
    gwInfo: apiIF.res.getCompositionsGatewayByGatewayID,
    ouID: string
  ) => {
    for (const unitInfo of gwInfo.compositions['rt-system-1']) {
      if (unitInfo['unit-id'] === ouID) {
        return unitInfo['unit-name'];
      }
    }
    for (const unitInfo of gwInfo.compositions['rt-system-2']) {
      if (unitInfo['unit-id'] === ouID) {
        return unitInfo['unit-name'];
      }
    }
    return '';
  };

  /**
   * Modbus室外機の名前を取得
   * @param gwInfo ゲートウェイの情報
   * @param ouID 室外機ID
   * @returns 室外機の名前
   */
  const modbusOUName = (
    gwInfo: apiIF.res.getCompositionsGatewayByGatewayID,
    ouID: string
  ) => {
    for (const unitInfo of gwInfo.compositions['modbus-system']) {
      if (unitInfo['outdoor-id'] === ouID) {
        return unitInfo['outdoor-unit-name'];
      }
      return '';
    }
  };

  return (
    <Box className="groupSetting__breadCrumbsSize">
      <Breadcrumbs aria-label="breadcrumb">
        {breadcrumbsLink.map((link, index) => {
          if (link.path === '/') {
            return (
              <Typography key={index} color="text.primary">
                {link.name}
              </Typography>
            );
          } else {
            return (
              <Link
                key={index}
                to={link.path}
                id={`mv-breadcrumbsLink${link.name}`}>
                {link.name}
              </Link>
            );
          }
        })}
      </Breadcrumbs>
    </Box>
  );
};
export default RegistrationBreadcrumbs;
